import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usersApi, tenantSettingsApi, authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SlideDrawer } from '../components/ui/slide-drawer';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Loader2, Plus, Users, Search, X, Mail, ChevronRight, Check, Lock, UserMinus, UserCheck, Trash2, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

// Strong temporary password generator (avoids ambiguous chars).
const genPassword = () => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ', lower = 'abcdefghijkmnpqrstuvwxyz', num = '23456789', sym = '!@#$%*?';
  const all = upper + lower + num + sym;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let p = [pick(upper), pick(lower), pick(num), pick(sym)];
  for (let i = 0; i < 10; i++) p.push(pick(all));
  return p.sort(() => Math.random() - 0.5).join('');
};
const pwChecks = (pw) => ({
  length: (pw || '').length >= 8,
  case: /[a-z]/.test(pw) && /[A-Z]/.test(pw),
  number: /\d/.test(pw),
  symbol: /[^A-Za-z0-9]/.test(pw),
});
const pwStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: '#E2DED4' };
  const c = pwChecks(pw); let score = Object.values(c).filter(Boolean).length;
  if (pw.length >= 14 && score === 4) score = 4; else if (score === 4 && pw.length < 12) score = 3;
  const map = { 1: { label: 'Weak', color: '#EF4444' }, 2: { label: 'Fair', color: '#E0B800' }, 3: { label: 'Good', color: '#FFD108' }, 4: { label: 'Strong', color: '#10B981' } };
  return { score, ...(map[score] || map[1]) };
};

// ── Role model ───────────────────────────────────────────────────────
const ROLE_META = {
  admin: { label: 'Admin', text: '#8A6D00', fill: '#FFF8D6', border: '#EFE0A0', dot: '#E0B800', paid: true, desc: 'Full access, including users, roles and billing.' },
  fleet_manager: { label: 'Fleet Manager', text: '#1A1A1A', fill: '#F2F0EA', border: '#DAD5CA', dot: '#6B6660', paid: true, desc: 'Runs the register, work orders and compliance.' },
  mechanic: { label: 'Mechanic', text: '#047857', fill: '#ECFDF5', border: '#A7F3D0', dot: '#10B981', paid: false, desc: 'Inspections, defects and work on the mobile app.' },
};
const ROLE_ORDER = ['admin', 'fleet_manager', 'mechanic'];
const rolesOf = (u) => {
  const raw = (u?.roles && u.roles.length > 0) ? u.roles : (u?.role ? [u.role] : ['mechanic']);
  return [...new Set(raw)];
};
const isOffice = (roles) => roles.includes('admin') || roles.includes('fleet_manager');
const DORMANT_DAYS = 45;

const CAPABILITIES = [
  { label: 'Complete inspections on mobile', admin: true, fleet_manager: true, mechanic: true },
  { label: 'Raise and close defects', admin: true, fleet_manager: true, mechanic: true },
  { label: 'Create and assign work orders', admin: true, fleet_manager: true, mechanic: false },
  { label: 'Edit the asset register', admin: true, fleet_manager: true, mechanic: false },
  { label: 'Upload certificates and documents', admin: true, fleet_manager: true, mechanic: false },
  { label: 'Manage users, roles and billing', admin: true, fleet_manager: false, mechanic: false },
];

const STATUS_META = {
  active: { label: 'Active', text: '#047857', dot: '#10B981', fill: '#ECFDF5', border: '#A7F3D0' },
  invited: { label: 'Invite pending', text: '#8A6D00', dot: '#E0B800', fill: '#FFF8D6', border: '#EFE0A0' },
  dormant: { label: 'Dormant', text: '#8F8A7F', dot: '#B5AFA3', fill: '#F2F0EA', border: '#DAD5CA' },
  deactivated: { label: 'Deactivated', text: '#B91C1C', dot: '#B91C1C', fill: '#FDECEC', border: '#F3C9C9' },
};

const relTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso); const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days <= 0) { const h = Math.floor(diff / 3600000); return h <= 1 ? 'Today' : 'Today'; }
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

// ── Small presentational atoms ────────────────────────────────────────
const RolePill = ({ role }) => {
  const m = ROLE_META[role] || ROLE_META.mechanic;
  return (
    <span className="inline-flex items-center gap-1.5 font-semibold" style={{ fontSize: 11.5, color: m.text, background: m.fill, border: `1px solid ${m.border}`, borderRadius: 999, padding: '2px 9px' }}>
      <span style={{ width: 5, height: 5, borderRadius: 999, background: m.dot }} />{m.label}
    </span>
  );
};
const StatusLine = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.active;
  return (
    <span className="inline-flex items-center gap-1.5" style={{ fontSize: 11, color: m.text }}>
      <span style={{ width: 5, height: 5, borderRadius: 999, background: m.dot }} />{m.label}
    </span>
  );
};
const Avatar = ({ name, isYou, size = 32 }) => (
  <div className="rounded-full flex items-center justify-center shrink-0 font-bold"
    style={{ width: size, height: size, fontSize: size * 0.42,
      background: isYou ? '#FFD108' : '#F6F4EF', border: `1px solid ${isYou ? '#E0B800' : '#E2DED4'}`, color: isYou ? '#000' : '#6B6660' }}>
    {(name || '?').charAt(0).toUpperCase()}
  </div>
);
const YouChip = () => <span className="font-bold" style={{ fontSize: 10, color: '#8A6D00', background: '#FFF8D6', border: '1px solid #EFE0A0', borderRadius: 999, padding: '1px 6px' }}>YOU</span>;
const FilterPills = ({ options, value, onChange, testId }) => (
  <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1 flex-wrap" data-testid={testId}>
    {options.map(o => {
      const active = value === o.id;
      return (
        <button key={o.id} type="button" onClick={() => onChange(o.id)} data-testid={o.testId}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${active ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
          {o.dot && <span className="w-2 h-2 rounded-full" style={{ background: o.dot }} />}
          {o.label}
          {o.count != null && <span className={`text-[11px] font-bold ${active ? 'text-black/70' : 'text-muted-foreground'}`}>{o.count}</span>}
        </button>
      );
    })}
  </div>
);

// ── Invite / Edit modal ───────────────────────────────────────────────
const UserFormModal = ({ mode, user, onClose, onSuccess, equiprAllocateEnabled, seat }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '', email: user?.email || '',
    roles: user ? rolesOf(user) : ['mechanic'], password: '',
    equipr_allocate_id: user?.equipr_allocate_id || '',
  });
  const toggleRole = (r) => {
    const has = form.roles.includes(r);
    if (has && form.roles.length === 1) { toast.info('Pick at least one role'); return; }
    setForm(p => ({ ...p, roles: has ? p.roles.filter(x => x !== r) : [...p.roles, r] }));
  };
  const officeSelected = isOffice(form.roles);
  const free = seat?.limit != null ? Math.max(0, seat.limit - (seat.used || 0) - (seat.held || 0)) : null;
  let consequence, tone = { bg: '#F6F4EF', border: '#E2DED4' };
  if (!officeSelected) consequence = 'Field user — free, and does not use an office seat.';
  else if (mode === 'edit') consequence = `Office user — already counted against your ${seat?.limit ?? '—'} seats.`;
  else if (free != null && free <= 0) { consequence = 'No office seats left. Adding this user needs an extra seat on your plan.'; tone = { bg: '#FFF8D6', border: '#EFE0A0' }; }
  else consequence = `Uses 1 office seat. ${free ?? '—'} of ${seat?.limit ?? '—'} currently free.`;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'invite') {
        await usersApi.invite({ name: form.name, email: form.email, roles: form.roles, password: form.password || undefined });
        toast.success('Invitation sent');
      } else {
        const data = { name: form.name, email: form.email, roles: form.roles };
        if (equiprAllocateEnabled) data.equipr_allocate_id = form.equipr_allocate_id || null;
        await usersApi.update(user.id, data);
        toast.success('User updated');
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally { setSubmitting(false); }
  };

  const roleFooter = form.roles.length === 0 ? 'Pick at least one role' : form.roles.map(r => ROLE_META[r]?.label).join(' + ');

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(26,26,26,0.45)' }} onClick={onClose} data-testid="user-form-modal">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="w-full max-w-[560px] mt-[6vh] flex flex-col"
        style={{ background: '#FBFAF7', border: '1px solid #DAD5CA', borderRadius: 14, boxShadow: '0 30px 70px -30px rgba(26,26,26,0.55)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E2D9]">
          <h2 className="font-extrabold uppercase tracking-tight" style={{ fontSize: 15, fontFamily: 'Manrope, sans-serif' }}>{mode === 'invite' ? 'Invite team member' : 'Edit user'}</h2>
          <button type="button" onClick={onClose} className="text-[#8F8A7F] hover:text-[#1A1A1A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4 overflow-y-auto" style={{ maxHeight: '70vh' }}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-1.5" style={{ fontSize: 11, fontWeight: 700 }}>Full name</label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Sarah Mitchell" required data-testid={`${mode}-name-input`} className="h-10 border-[#C9C3B6] rounded-lg" />
            </div>
            <div>
              <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-1.5" style={{ fontSize: 11, fontWeight: 700 }}>Email</label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="name@company.com" required data-testid={`${mode}-email-input`} className="h-10 border-[#C9C3B6] rounded-lg" />
            </div>
          </div>

          <div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="uppercase tracking-[0.08em] text-[#6B6660]" style={{ fontSize: 11, fontWeight: 700 }}>Roles</span>
              <span className="text-[#8F8A7F]" style={{ fontSize: 12 }}>Pick one or more. A person can be a fleet manager and a mechanic.</span>
            </div>
            <div className="space-y-2">
              {ROLE_ORDER.map(r => {
                const m = ROLE_META[r]; const sel = form.roles.includes(r);
                return (
                  <button type="button" key={r} onClick={() => toggleRole(r)} data-testid={`${mode}-role-${r}`}
                    className="w-full flex items-center gap-3 text-left transition-colors"
                    style={{ padding: '11px 13px', borderRadius: 9, background: sel ? '#FFFDF2' : '#fff', border: `1px solid ${sel ? '#E0B800' : '#E2DED4'}` }}>
                    <span className="flex items-center justify-center shrink-0" style={{ width: 20, height: 20, borderRadius: 6, background: sel ? '#FFD108' : '#fff', border: `1px solid ${sel ? '#E0B800' : '#C9C3B6'}` }}>
                      {sel && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold text-[#1A1A1A]" style={{ fontSize: 13.5 }}>{m.label}</span>
                      <span className="block text-[#6B6660]" style={{ fontSize: 12 }}>{m.desc}</span>
                    </span>
                    <span className="font-semibold shrink-0" style={{ fontSize: 10.5, borderRadius: 999, padding: '2px 8px', color: m.paid ? '#8A6D00' : '#047857', background: m.paid ? '#FFF8D6' : '#ECFDF5' }}>
                      {m.paid ? 'PAID SEAT' : 'FREE'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {mode === 'invite' && (() => {
            const st = pwStrength(form.password); const c = pwChecks(form.password);
            const reqs = [['length', '8+ characters'], ['case', 'Upper & lower case'], ['number', 'A number'], ['symbol', 'A symbol']];
            return (
              <div>
                <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-1.5" style={{ fontSize: 11, fontWeight: 700 }}>Temporary password</label>
                <div className="flex items-center gap-2">
                  <Input type="text" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Auto-generated if left blank" data-testid="invite-password-input" className="h-10 border-[#C9C3B6] rounded-lg font-mono flex-1" />
                  <Button type="button" variant="outline" onClick={() => setForm(p => ({ ...p, password: genPassword() }))} className="h-10 shrink-0" data-testid="generate-password-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Generate</Button>
                </div>
                {form.password && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1 flex-1">
                        {[1, 2, 3, 4].map(i => <span key={i} className="flex-1 h-1.5 rounded-full transition-colors" style={{ background: i <= st.score ? st.color : '#EDEAE3' }} />)}
                      </div>
                      <span className="font-semibold" style={{ fontSize: 11, color: st.color === '#FFD108' ? '#8A6D00' : st.color }}>{st.label}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                      {reqs.map(([k, lbl]) => (
                        <span key={k} className="inline-flex items-center gap-1.5" style={{ fontSize: 11.5, color: c[k] ? '#047857' : '#8F8A7F' }}>
                          {c[k] ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <span className="w-3 h-3 rounded-full border border-[#C9C3B6] inline-block" />}{lbl}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-[#8F8A7F] mt-2" style={{ fontSize: 11.5 }}>Leave blank to auto-generate. The user is prompted to change it on first sign-in.</p>
              </div>
            );
          })()}

          {equiprAllocateEnabled && (
            <div>
              <div className="flex items-baseline gap-2 mb-1.5">
                <span className="uppercase tracking-[0.08em] text-[#6B6660]" style={{ fontSize: 11, fontWeight: 700 }}>EQUIPR Allocate ID</span>
                <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>Optional</span>
              </div>
              <Input value={form.equipr_allocate_id} onChange={e => setForm(p => ({ ...p, equipr_allocate_id: e.target.value }))} placeholder="ALLOC-00000" data-testid="edit-allocate-id-input" className="h-10 border-[#C9C3B6] rounded-lg font-mono" />
              <p className="text-[#8F8A7F] mt-1" style={{ fontSize: 11.5 }}>Links this person to their record in EQUIPR Allocate so hours and jobs match up.</p>
            </div>
          )}

          <div className="flex items-center gap-2 text-[#3D3A35]" style={{ padding: '11px 13px', borderRadius: 9, fontSize: 12.5, background: tone.bg, border: `1px solid ${tone.border}` }} data-testid="seat-consequence">
            <Users className="w-4 h-4 shrink-0 text-[#6B6660]" />{consequence}
          </div>
        </div>
        <div className="flex items-center gap-3 px-5 py-3.5 border-t border-[#DAD5CA] bg-white rounded-b-[14px]">
          <Button type="submit" disabled={submitting || form.roles.length === 0} data-testid={`submit-${mode}-btn`} className="btn-industrial h-[38px]" style={{ width: 150 }}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{mode === 'invite' ? 'Send invite' : 'Save changes'}
          </Button>
          <Button type="button" variant="outline" onClick={onClose} className="h-[38px]">Cancel</Button>
          <span className="ml-auto text-[#8F8A7F]" style={{ fontSize: 12 }}>{roleFooter}</span>
        </div>
      </form>
    </div>
  );
};

// ── Detail drawer body ────────────────────────────────────────────────
const DetailBody = ({ item, isYou, equiprAllocateEnabled, formatDate, onEdit, onReset, onLifecycle, onResend, onCancelInvite }) => {
  const roles = rolesOf(item);
  const status = item.__status;
  const sm = STATUS_META[status];
  const office = isOffice(roles);
  const allowed = (cap) => roles.some(r => cap[r]);
  const grantRole = (cap) => ROLE_ORDER.find(r => roles.includes(r) && cap[r]);
  const allowedCount = CAPABILITIES.filter(allowed).length;
  const lastActiveMeta = item.kind === 'invite' ? `Sent ${formatDate(item.created_at)}` : (item.last_login_at ? `Last active ${relTime(item.last_login_at)}` : 'Never signed in');

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {/* Identity */}
        <div className="flex items-center gap-3">
          <Avatar name={item.name} isYou={isYou} size={52} />
          <div className="min-w-0">
            <div className="flex items-center gap-2"><h2 className="font-bold truncate" style={{ fontSize: 19, fontFamily: 'Manrope, sans-serif' }}>{item.name}</h2>{isYou && <YouChip />}</div>
            <p className="text-[#6B6660] truncate" style={{ fontSize: 12.5 }}>{item.email}</p>
          </div>
        </div>

        {/* Status strip */}
        <div className="flex items-center justify-between" style={{ padding: '10px 14px', borderRadius: 10, background: sm.fill, border: `1px solid ${sm.border}`, borderLeft: `3px solid ${sm.dot}` }}>
          <span className="font-semibold flex items-center gap-2" style={{ fontSize: 13, color: sm.text }}><span style={{ width: 6, height: 6, borderRadius: 999, background: sm.dot }} />{sm.label}</span>
          <span className="font-mono text-[#6B6660]" style={{ fontSize: 12 }}>{lastActiveMeta}</span>
        </div>

        {/* Invite callout */}
        {item.kind === 'invite' && (
          <div style={{ padding: '12px 14px', borderRadius: 10, background: '#fff', border: '1px dashed #C9C3B6' }}>
            <p className="font-semibold text-[#1A1A1A]" style={{ fontSize: 13 }}>Invite not accepted yet</p>
            <p className="text-[#6B6660] mt-0.5" style={{ fontSize: 12 }}>Sent {formatDate(item.created_at)}{item.expires_at ? ` · expires ${formatDate(item.expires_at)}` : ''}{office ? ' · holding an office seat' : ''}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => onResend(item.__raw)}><Mail className="w-3.5 h-3.5 mr-1.5" />Resend</Button>
              <Button variant="ghost" size="sm" className="text-[#B91C1C]" onClick={() => onCancelInvite(item.__raw)}>Cancel invite</Button>
            </div>
          </div>
        )}

        {/* User information */}
        <div style={{ border: '1px solid #E6E2D9', borderRadius: 10, overflow: 'hidden' }}>
          <div className="px-3.5 py-2.5 bg-[#F6F4EF] border-b border-[#E6E2D9] uppercase tracking-[0.09em] text-[#6B6660]" style={{ fontSize: 10.5, fontWeight: 700 }}>User information</div>
          {[
            ['Email', item.email],
            ['Roles', roles.map(r => ROLE_META[r]?.label).join(', ')],
            ['Seat type', office ? 'Office user (paid seat)' : 'Field user (free, unlimited)'],
            ['Joined', formatDate(item.created_at)],
            ['Last active', item.kind === 'invite' ? 'Never' : (item.last_login_at ? relTime(item.last_login_at) : 'Never')],
            ...(equiprAllocateEnabled ? [['EQUIPR Allocate ID', item.equipr_allocate_id || 'Not linked']] : []),
          ].map(([k, v], i) => (
            <div key={i} className="grid items-center border-b border-[#F4F2ED] last:border-0" style={{ gridTemplateColumns: '150px 1fr', padding: '10px 14px' }}>
              <span className="text-[#8F8A7F]" style={{ fontSize: 12 }}>{k}</span>
              <span className="text-[#1A1A1A]" style={{ fontSize: 13, color: v === 'Not linked' ? '#B5AFA3' : '#1A1A1A' }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Permissions */}
        <div style={{ border: '1px solid #E6E2D9', borderRadius: 10, overflow: 'hidden' }}>
          <div className="flex items-center justify-between px-3.5 py-2.5 bg-[#F6F4EF] border-b border-[#E6E2D9]">
            <span className="uppercase tracking-[0.09em] text-[#6B6660]" style={{ fontSize: 10.5, fontWeight: 700 }}>What this user can do</span>
            <span className="font-mono text-[#8F8A7F]" style={{ fontSize: 11 }}>{allowedCount} of 6</span>
          </div>
          {CAPABILITIES.map((cap, i) => {
            const ok = allowed(cap); const gr = grantRole(cap);
            return (
              <div key={i} className="flex items-center gap-3 border-b border-[#F4F2ED] last:border-0" style={{ padding: '9px 14px' }}>
                {ok ? <Check className="w-4 h-4 shrink-0" style={{ color: '#10B981' }} strokeWidth={3} /> : <X className="w-4 h-4 shrink-0" style={{ color: '#C9C3B6' }} />}
                <span className="flex-1" style={{ fontSize: 12.5, color: ok ? '#1A1A1A' : '#B5AFA3' }}>{cap.label}</span>
                <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>{ok ? ROLE_META[gr]?.label : 'Not permitted'}</span>
              </div>
            );
          })}
        </div>

        {/* Recent activity */}
        <div style={{ border: '1px solid #E6E2D9', borderRadius: 10, overflow: 'hidden' }}>
          <div className="px-3.5 py-2.5 bg-[#F6F4EF] border-b border-[#E6E2D9] uppercase tracking-[0.09em] text-[#6B6660]" style={{ fontSize: 10.5, fontWeight: 700 }}>Recent activity</div>
          {item.kind === 'invite' ? (
            <div className="px-3.5 py-4 text-[#8F8A7F]" style={{ fontSize: 12.5 }}>No activity recorded yet.</div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-[#F4F2ED]" style={{ padding: '10px 14px' }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: item.last_login_at ? '#10B981' : '#B5AFA3' }} />
                <span className="flex-1 text-[#1A1A1A]" style={{ fontSize: 12.5 }}>Last sign-in<span className="block font-mono text-[#8F8A7F]" style={{ fontSize: 10.5 }}>{item.last_login_at ? relTime(item.last_login_at) : 'never'}</span></span>
              </div>
              <div className="flex items-center gap-3" style={{ padding: '10px 14px' }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: '#B5AFA3' }} />
                <span className="flex-1 text-[#1A1A1A]" style={{ fontSize: 12.5 }}>Account created<span className="block font-mono text-[#8F8A7F]" style={{ fontSize: 10.5 }}>{formatDate(item.created_at)}</span></span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Action bar */}
      {!isYou && item.kind !== 'invite' && (
        <div className="flex items-center gap-2 mt-4 pt-3.5 border-t border-[#DAD5CA]">
          <Button onClick={onEdit} className="btn-industrial h-[38px]" data-testid="edit-user-btn">Edit user</Button>
          <Button variant="outline" onClick={onReset} className="h-[38px]"><Lock className="w-4 h-4 mr-1.5" />Reset password</Button>
          <Button variant="outline" onClick={() => onLifecycle(item.is_active === false ? 'reactivate' : 'deactivate', item)}
            className="h-[38px] ml-auto" style={{ color: '#B91C1C', borderColor: '#E3B5AF' }}>
            {item.is_active === false ? <><UserCheck className="w-4 h-4 mr-1.5" />Reactivate user</> : <><UserMinus className="w-4 h-4 mr-1.5" />Deactivate user</>}
          </Button>
        </div>
      )}
    </div>
  );
};

// ── Page ───────────────────────────────────────────────────────────────
export const UsersListPage = () => {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [seat, setSeat] = useState(null);
  const [seatError, setSeatError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sort, setSort] = useState('name');
  const [equiprAllocateEnabled, setEquiprAllocateEnabled] = useState(false);

  const [drawerItem, setDrawerItem] = useState(null);
  const [modal, setModal] = useState(null); // {mode:'invite'|'edit', user}
  const [lifecycle, setLifecycle] = useState(null); // {action, user}
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [lifecycleRefs, setLifecycleRefs] = useState(null);
  const [inviteAction, setInviteAction] = useState(null); // {invite, type}
  const [inviteBusy, setInviteBusy] = useState(false);

  const { isAdmin, user: currentUser } = useAuth();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { loadAll(); loadIntegrations();
    if (searchParams.get('invite') === '1') { setModal({ mode: 'invite' }); const n = new URLSearchParams(searchParams); n.delete('invite'); setSearchParams(n, { replace: true }); }
  }, []); // eslint-disable-line

  const loadAll = async () => {
    try {
      const [u, inv, s] = await Promise.all([
        usersApi.getAll(), usersApi.getPendingInvites().catch(() => ({ data: [] })), usersApi.getSeatUsage().catch(() => null),
      ]);
      setUsers(u.data || []); setInvites(inv.data || []);
      if (s) { setSeat(s.data); setSeatError(false); } else { setSeatError(true); }
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };
  const loadIntegrations = async () => {
    // EQUIPR Allocate ID is hidden for now (product decision), regardless of
    // whether the tenant integration is enabled.
    setEquiprAllocateEnabled(false);
  };

  // Build unified register
  const register = useMemo(() => {
    const now = Date.now();
    const uItems = users.map(u => {
      const roles = rolesOf(u);
      let status = 'active';
      if (u.is_active === false) status = 'deactivated';
      else if (u.last_login_at && (now - new Date(u.last_login_at).getTime()) >= DORMANT_DAYS * 86400000) status = 'dormant';
      return { ...u, kind: 'user', __status: status, __roles: roles };
    });
    const iItems = invites.map(inv => ({
      kind: 'invite', id: `invite-${inv.id}`, name: inv.name || inv.email, email: inv.email,
      roles: inv.roles || ['mechanic'], is_active: true, created_at: inv.created_at, expires_at: inv.expires_at,
      last_login_at: null, __status: 'invited', __roles: inv.roles || ['mechanic'], __raw: inv,
    }));
    return [...uItems, ...iItems];
  }, [users, invites]);

  const held = useMemo(() => invites.filter(i => isOffice(i.roles || [])).length, [invites]);
  const roleCounts = useMemo(() => {
    const c = { admin: 0, fleet_manager: 0, mechanic: 0 };
    register.forEach(it => it.__roles.forEach(r => { if (c[r] != null) c[r]++; }));
    return c;
  }, [register]);
  const statusCounts = useMemo(() => {
    const c = { all: register.length, active: 0, invited: 0, dormant: 0, deactivated: 0 };
    register.forEach(it => { c[it.__status] = (c[it.__status] || 0) + 1; });
    return c;
  }, [register]);

  const filtered = useMemo(() => {
    let list = register.filter(it => {
      const q = search.toLowerCase();
      const matchesSearch = !q || it.name?.toLowerCase().includes(q) || it.email?.toLowerCase().includes(q) || (it.equipr_allocate_id || '').toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || it.__status === statusFilter;
      const matchesRole = roleFilter === 'all' || it.__roles.includes(roleFilter);
      return matchesSearch && matchesStatus && matchesRole;
    });
    list.sort((a, b) => {
      if (sort === 'last_active') {
        const av = a.last_login_at ? new Date(a.last_login_at).getTime() : 0;
        const bv = b.last_login_at ? new Date(b.last_login_at).getTime() : 0;
        return bv - av;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
    return list;
  }, [register, search, statusFilter, roleFilter, sort]);

  const dormantOver = statusCounts.dormant || 0;
  const invitesPending = statusCounts.invited || 0;
  const hasFilters = search || statusFilter !== 'all' || roleFilter !== 'all';

  // Lifecycle
  const openLifecycle = async (action, u) => {
    setLifecycle({ action, user: u }); setLifecycleRefs(null);
    if (action === 'delete') { try { const r = await usersApi.getReferences(u.id); setLifecycleRefs(r.data); } catch (e) { toast.error('Failed to check references'); setLifecycle(null); } }
  };
  const confirmLifecycle = async () => {
    const { action, user: u } = lifecycle; setLifecycleBusy(true);
    try {
      if (action === 'deactivate') { await usersApi.deactivate(u.id); toast.success(`${u.name} deactivated`); }
      else if (action === 'reactivate') { await usersApi.reactivate(u.id); toast.success(`${u.name} reactivated`); }
      else if (action === 'delete') { await usersApi.delete(u.id); toast.success(`${u.name} deleted`); }
      setLifecycle(null); setDrawerItem(null); loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Action failed'));
      if (err.response?.status === 409 && d?.references) setLifecycleRefs({ references: d.references, can_hard_delete: false });
    } finally { setLifecycleBusy(false); }
  };
  const resetPassword = async (item) => {
    try { await authApi.forgotPassword(item.email); toast.success(`Password reset link sent to ${item.email}`); }
    catch { toast.success('If that email exists, a reset link has been sent'); }
  };
  const handleResend = async (inv) => { setInviteBusy(true); try { await usersApi.resendInvite(inv.id); toast.success(`Invitation resent to ${inv.email}`); setInviteAction(null); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setInviteBusy(false); } };
  const handleCancel = async (inv) => { setInviteBusy(true); try { await usersApi.cancelInvite(inv.id); toast.success('Invitation cancelled'); setInviteAction(null); setDrawerItem(null); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setInviteBusy(false); } };

  if (!isAdmin()) { navigate('/dashboard'); return null; }

  const limit = seat?.limit; const used = seat?.used || 0;
  const free = limit != null ? Math.max(0, limit - used - held) : null;
  const atLimit = limit != null && free <= 0;
  const accent = atLimit ? '#E0B800' : '#10B981';

  return (
    <div className="flex flex-col gap-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>User management</h1>
          <p className="text-muted-foreground">Who can see your fleet, and what they are allowed to do in it</p>
        </div>
        <Button onClick={() => setModal({ mode: 'invite' })} className="btn-industrial" data-testid="invite-user-btn"><Plus className="w-5 h-5 mr-2" />Invite user</Button>
      </div>

      {/* Seat + people-by-role */}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(330px,1.15fr) minmax(0,1fr)' }}>
        <div className="bg-white" style={{ border: '1px solid #DAD5CA', borderRadius: 11, padding: '14px 16px', borderLeft: `3px solid ${accent}` }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="uppercase tracking-[0.1em] text-[#6B6660]" style={{ fontSize: 10.5, fontWeight: 700 }}>Office user seats</span>
              <span className="font-mono" style={{ fontSize: 9.5, color: '#8A6D00', background: '#FFF8D6', borderRadius: 4, padding: '1px 5px' }}>{(seat?.plan || 'pro') === 'free' ? 'Free' : 'Pro'}</span>
            </div>
            <button onClick={() => navigate('/settings?tab=billing')} className="text-[#8A6D00] hover:underline" style={{ fontSize: 11.5 }}>Manage plan</button>
          </div>
          {seatError ? (
            <p className="text-[#8F8A7F] mt-2" style={{ fontSize: 12.5 }}>— Seat usage unavailable</p>
          ) : !seat ? (
            <div className="mt-2 h-8 rounded animate-pulse bg-[#F2F0EA]" />
          ) : (
            <>
              <div className="flex items-baseline justify-between mt-1.5">
                <div className="flex items-baseline gap-2">
                  <span className="font-mono font-bold text-[#1A1A1A]" style={{ fontSize: 30 }}>{used}</span>
                  <span className="text-[#8F8A7F]" style={{ fontSize: 14 }}>of {limit ?? '∞'} used</span>
                </div>
                <span className="font-semibold" style={{ fontSize: 12, color: accent === '#E0B800' ? '#8A6D00' : '#047857' }}>{limit == null ? 'Unlimited' : (atLimit ? 'No seats left' : `${free} free`)}</span>
              </div>
              {limit != null && (
                <>
                  <div className="flex gap-1 mt-2.5">
                    {Array.from({ length: limit }).map((_, i) => {
                      const cell = i < used ? { bg: '#1A1A1A', bd: '#1A1A1A' } : i < used + held ? { bg: '#FFF0A8', bd: '#E0B800' } : { bg: '#F2F0EA', bd: '#E2DED4' };
                      return <span key={i} style={{ flex: 1, height: 8, borderRadius: 3, background: cell.bg, border: `1px solid ${cell.bd}` }} />;
                    })}
                  </div>
                  <div className="flex gap-4 mt-2.5 text-[#6B6660]" style={{ fontSize: 11.5 }}>
                    <span className="flex items-center gap-1.5"><span style={{ width: 9, height: 9, borderRadius: 2, background: '#1A1A1A' }} />{used} in use</span>
                    <span className="flex items-center gap-1.5"><span style={{ width: 9, height: 9, borderRadius: 2, background: '#FFF0A8', border: '1px solid #E0B800' }} />{held} held by invite</span>
                    <span className="flex items-center gap-1.5"><span style={{ width: 9, height: 9, borderRadius: 2, background: '#F2F0EA', border: '1px solid #E2DED4' }} />{free} free</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))' }}>
          {ROLE_ORDER.map(r => {
            const m = ROLE_META[r]; const active = roleFilter === r;
            return (
              <button key={r} onClick={() => setRoleFilter(active ? 'all' : r)} data-testid={`role-tile-${r}`}
                className="text-left bg-white transition-colors" style={{ border: `1px solid ${active ? '#E0B800' : '#DAD5CA'}`, background: active ? '#FFFDF2' : '#fff', borderRadius: 11, padding: '12px 14px' }}>
                <span className="flex items-center gap-1.5 font-semibold text-[#1A1A1A]" style={{ fontSize: 11.5 }}><span style={{ width: 6, height: 6, borderRadius: 999, background: m.dot }} />{m.label}</span>
                <span className="block font-mono font-bold text-[#1A1A1A]" style={{ fontSize: 21, marginTop: 2 }}>{roleCounts[r]}</span>
                <span className="block text-[#8F8A7F]" style={{ fontSize: 11 }}>{m.paid ? 'Paid office seat' : 'Field user, free'}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Register */}
      <div className="bg-white overflow-hidden" style={{ border: '1px solid #DAD5CA', borderRadius: 12, boxShadow: '0 1px 2px rgba(26,26,26,0.05), 0 8px 20px -14px rgba(26,26,26,0.16)' }}>
        {/* Filter row */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3" style={{ padding: '13px 14px', borderBottom: '1px solid #E6E2D9' }}>
          <div className="relative w-full lg:max-w-[400px] lg:min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8F8A7F]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={equiprAllocateEnabled ? 'Name, email or Allocate ID' : 'Name or email'} data-testid="user-search-input" className="pl-10 pr-14 h-[42px] border-[1.5px] border-[#1A1A1A] rounded-[9px]" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[#8F8A7F]" style={{ fontSize: 10.5 }}>{filtered.length}/{register.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-[0.08em] text-[#6B6660]" style={{ fontSize: 11, fontWeight: 700 }}>Sort</span>
            <FilterPills value={sort} onChange={setSort} testId="user-sort" options={[{ id: 'name', label: 'Name', testId: 'sort-name' }, { id: 'last_active', label: 'Last active', testId: 'sort-last-active' }]} />
          </div>
        </div>
        {/* Status + role chips */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2" style={{ padding: '11px 14px', borderBottom: '1px solid #E6E2D9' }}>
          <span className="uppercase tracking-[0.08em] text-[#6B6660]" style={{ fontSize: 11, fontWeight: 700 }}>Status</span>
          <FilterPills value={statusFilter} onChange={setStatusFilter} testId="status-filter" options={[
            { id: 'all', label: 'All', count: statusCounts.all, testId: 'status-all' },
            { id: 'active', label: 'Active', count: statusCounts.active, dot: '#10B981', testId: 'status-active' },
            { id: 'invited', label: 'Invite pending', count: statusCounts.invited, dot: '#E0B800', testId: 'status-invited' },
            { id: 'dormant', label: 'Dormant', count: statusCounts.dormant, dot: '#B5AFA3', testId: 'status-dormant' },
            { id: 'deactivated', label: 'Deactivated', count: statusCounts.deactivated, dot: '#B91C1C', testId: 'status-deactivated' },
          ]} />
          <span className="uppercase tracking-[0.08em] text-[#6B6660] ml-2" style={{ fontSize: 11, fontWeight: 700 }}>Role</span>
          <FilterPills value={roleFilter} onChange={setRoleFilter} testId="role-filter" options={[
            { id: 'all', label: 'All', count: register.length, testId: 'role-all' },
            ...ROLE_ORDER.map(r => ({ id: r, label: ROLE_META[r].label, count: roleCounts[r], testId: `role-${r}` })),
          ]} />
          {hasFilters && <button onClick={() => { setSearch(''); setStatusFilter('all'); setRoleFilter('all'); }} className="text-[#8F8A7F] hover:text-[#1A1A1A] flex items-center gap-1" style={{ fontSize: 12 }} data-testid="clear-filters-btn"><X className="w-3.5 h-3.5" />Clear filters</button>}
        </div>
        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center"><Users className="w-12 h-12 mx-auto text-[#C9C3B6] mb-3" /><h3 className="font-bold text-[#1A1A1A]" style={{ fontSize: 16 }}>No users found</h3><p className="text-[#8F8A7F]" style={{ fontSize: 12.5 }}>No team members match the current filters.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <div style={{ minWidth: 1180 }}>
              <div className="grid items-center bg-[#F6F4EF] uppercase tracking-[0.1em] text-[#6B6660]" style={{ gridTemplateColumns: 'minmax(210px,1.3fr) minmax(190px,1.2fr) minmax(200px,1.3fr) 112px 116px 96px 40px', height: 32, padding: '0 14px', fontSize: 10, borderBottom: '1px solid #DAD5CA' }}>
                <span style={{ color: sort === 'name' ? '#1A1A1A' : undefined, fontWeight: sort === 'name' ? 700 : 500 }}>Name</span><span>Email</span><span>Roles</span><span>Seat</span>
                <span style={{ color: sort === 'last_active' ? '#1A1A1A' : undefined, fontWeight: sort === 'last_active' ? 700 : 500 }}>Last active</span><span>Joined</span><span />
              </div>
              {filtered.map(it => {
                const isYou = it.id === currentUser?.id;
                const deact = it.__status === 'deactivated';
                const office = isOffice(it.__roles);
                const la = it.kind === 'invite' ? 'Never' : deact ? 'Disabled' : (it.last_login_at ? relTime(it.last_login_at) : 'Never');
                return (
                  <div key={it.id} onClick={() => setDrawerItem(it)} data-testid={`user-row-${it.id}`}
                    className="grid items-center cursor-pointer transition-colors hover:bg-[#FFFCEF]"
                    style={{ gridTemplateColumns: 'minmax(210px,1.3fr) minmax(190px,1.2fr) minmax(200px,1.3fr) 112px 116px 96px 40px', padding: '9px 14px', borderBottom: '1px solid #F0EDE6', background: deact ? '#FBFAF7' : undefined }}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar name={it.name} isYou={isYou} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5"><span className="font-semibold truncate" style={{ fontSize: 13, color: deact ? '#8F8A7F' : '#1A1A1A' }}>{it.name}</span>{isYou && <YouChip />}</div>
                        <StatusLine status={it.__status} />
                      </div>
                    </div>
                    <span className="truncate" style={{ fontSize: 12.5, color: deact ? '#B5AFA3' : '#3D3A35' }}>{it.email}</span>
                    <div className="flex flex-wrap gap-1.5">{it.__roles.map(r => <RolePill key={r} role={r} />)}</div>
                    <span style={{ fontSize: 12, color: office ? '#1A1A1A' : '#6B6660' }}>{office ? 'Office · paid' : 'Field · free'}</span>
                    <span className="font-mono" style={{ fontSize: 11.5, color: deact ? '#B5AFA3' : '#3D3A35' }}>{la}</span>
                    <span className="font-mono text-[#6B6660]" style={{ fontSize: 11.5 }}>{formatDate(it.created_at)}</span>
                    <ChevronRight className="w-[15px] h-[15px]" style={{ color: '#B5AFA3' }} />
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Footer */}
        <div className="flex items-center justify-between" style={{ padding: '11px 14px', borderTop: '1px solid #E6E2D9', background: '#FBFAF7' }}>
          <span className="text-[#6B6660]" style={{ fontSize: 12 }}>Showing {filtered.length} of {register.length} users</span>
          <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>{invitesPending} invites pending · {dormantOver} dormant over {DORMANT_DAYS} days</span>
        </div>
      </div>

      {/* Detail drawer */}
      <SlideDrawer open={!!drawerItem} onOpenChange={(o) => { if (!o) setDrawerItem(null); }}
        title="User" dismissable
        titleExtra={drawerItem && equiprAllocateEnabled && <span className="font-mono text-[#8F8A7F]" style={{ fontSize: 11 }}>{drawerItem.equipr_allocate_id || 'No Allocate ID'}</span>} size="lg">
        {drawerItem && (
          <DetailBody item={drawerItem} isYou={drawerItem.id === currentUser?.id} equiprAllocateEnabled={equiprAllocateEnabled} formatDate={formatDate}
            onEdit={() => { const u = drawerItem; setDrawerItem(null); setModal({ mode: 'edit', user: u }); }}
            onReset={() => resetPassword(drawerItem)}
            onLifecycle={openLifecycle} onResend={handleResend} onCancelInvite={(inv) => setInviteAction({ invite: inv, type: 'cancel' })} />
        )}
      </SlideDrawer>

      {/* Invite / edit modal */}
      {modal && (
        <UserFormModal mode={modal.mode} user={modal.user} equiprAllocateEnabled={equiprAllocateEnabled}
          seat={{ used, limit, held }} onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); loadAll(); }} />
      )}

      {/* Lifecycle dialog */}
      <AlertDialog open={!!lifecycle} onOpenChange={(o) => { if (!o && !lifecycleBusy) { setLifecycle(null); setLifecycleRefs(null); } }}>
        <AlertDialogContent data-testid="user-lifecycle-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {lifecycle?.action === 'deactivate' && `Deactivate ${lifecycle?.user?.name}?`}
              {lifecycle?.action === 'reactivate' && `Reactivate ${lifecycle?.user?.name}?`}
              {lifecycle?.action === 'delete' && `Permanently delete ${lifecycle?.user?.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                {lifecycle?.action === 'deactivate' && <p>The user will be unable to log in and won't appear in assignment pickers. Their existing work orders, defects and inspections stay intact. You can reactivate them at any time.</p>}
                {lifecycle?.action === 'reactivate' && <p>This user will regain access immediately and will be available for new assignments.</p>}
                {lifecycle?.action === 'delete' && lifecycleRefs && !lifecycleRefs.can_hard_delete && (
                  <div className="space-y-2"><p className="text-destructive font-medium">This user cannot be permanently deleted — they have historical records.</p><p>Use <strong>Deactivate</strong> instead — it blocks login but keeps the audit trail.</p></div>
                )}
                {lifecycle?.action === 'delete' && lifecycleRefs && lifecycleRefs.can_hard_delete && <p className="text-destructive font-medium">This action is permanent and cannot be undone.</p>}
                {lifecycle?.action === 'delete' && !lifecycleRefs && <p>Checking for historical records…</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={lifecycleBusy} data-testid="lifecycle-cancel-btn">Cancel</AlertDialogCancel>
            {lifecycle?.action === 'delete' && lifecycleRefs && !lifecycleRefs.can_hard_delete ? (
              <AlertDialogAction onClick={(e) => { e.preventDefault(); openLifecycle('deactivate', lifecycle.user); }} data-testid="lifecycle-switch-to-deactivate-btn">Deactivate instead</AlertDialogAction>
            ) : (
              <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmLifecycle(); }} disabled={lifecycleBusy || (lifecycle?.action === 'delete' && !lifecycleRefs)}
                className={lifecycle?.action === 'delete' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''} data-testid="lifecycle-confirm-btn">
                {lifecycleBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {lifecycle?.action === 'deactivate' && 'Deactivate'}{lifecycle?.action === 'reactivate' && 'Reactivate'}{lifecycle?.action === 'delete' && 'Delete permanently'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resend / cancel invite */}
      <AlertDialog open={!!inviteAction} onOpenChange={(o) => { if (!o) setInviteAction(null); }}>
        <AlertDialogContent data-testid={`invite-action-${inviteAction?.type || 'dialog'}`}>
          {inviteAction && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{inviteAction.type === 'resend' ? 'Resend invitation?' : 'Cancel invitation?'}</AlertDialogTitle>
                <AlertDialogDescription>{inviteAction.type === 'resend' ? `Re-send the invitation email to ${inviteAction.invite.email}? The link in any previous email will continue to work, and the expiry will be refreshed for another 48 hours.` : `Cancel the pending invitation for ${inviteAction.invite.email}? They will no longer be able to use the link in their inbox to join.`}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={inviteBusy} data-testid="invite-action-cancel">Back</AlertDialogCancel>
                <AlertDialogAction disabled={inviteBusy} onClick={() => inviteAction.type === 'resend' ? handleResend(inviteAction.invite) : handleCancel(inviteAction.invite)} data-testid="invite-action-confirm"
                  className={inviteAction.type === 'cancel' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}>{inviteBusy ? 'Working…' : (inviteAction.type === 'resend' ? 'Resend email' : 'Cancel invite')}</AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersListPage;
